/**
 * 조직 탭(연결 관리 안) — 내 조직 목록 · 만들기 · 초대코드로 신청 · 구성원(관리자: 승인·역할·내보내기·초대코드) (2026-10-03).
 * 서비스 운영자와 다른 층이다 — 여기서 보이는 것은 **내가 든 조직**뿐이다. 스탠드얼론에서는 탭 자체가 없다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiUsers, FiPlus, FiKey, FiRefreshCw, FiCheck, FiX, FiLogOut, FiUserPlus, FiCopy, FiShield } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { formatApiError } from '../../utils/formatters';
import { useConfirm } from '../ConfirmDialog';
import { usePrompt } from '../PromptDialog';
import { sharesApi } from '../sharing/sharesApi';
import './OrgPanel.css';

export default function OrgPanel({ api, apiBase, meUsrid, onChanged }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const share = useMemo(() => sharesApi(apiBase), [apiBase]);
  const [orgs, setOrgs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const list = await api.mine();
      setOrgs(list);
      setSelectedId(prev => (list.some(o => o.id === prev) ? prev : ((list.find(o => o.status === 'active') || {}).id || null)));
    } catch (err) { toast.error(formatApiError(err, t)); }
  }, [api, t]);
  useEffect(() => { load(); }, [load]);

  const selected = orgs.find(o => o.id === selectedId) || null;
  const isAdmin = !!(selected && selected.status === 'active' && selected.role === 'admin');

  const loadDetail = useCallback(async () => {
    if (!selected || selected.status !== 'active') { setMembers([]); setInviteCode(null); return; }
    try {
      setMembers(await api.members(selected.id));
      setInviteCode(isAdmin ? await api.inviteCode(selected.id) : null);
    } catch (err) { toast.error(formatApiError(err, t)); }
  }, [api, selected, isAdmin, t]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  // 사용자 검색(관리자가 바로 넣을 때) — 수집 조회 공유와 같은 디렉터리
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setFound([]); return undefined; }
    timer.current = setTimeout(async () => {
      try { setFound((await share.searchUsers(q.trim())).filter(u => !members.some(m => m.usrid === u.usrid))); } catch (_e) { setFound([]); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, share, members]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); await loadDetail(); if (onChanged) onChanged(); }
    catch (err) { toast.error(formatApiError(err, t)); }
    finally { setBusy(false); }
  };
  const createOrg = async () => {
    const name = await prompt({ title: t('common:org.createTitle'), message: t('common:org.createMessage'), placeholder: t('common:org.namePlaceholder') });
    if (!name) return;
    await run(() => api.create(name), t('common:org.created', { name }));
  };
  const joinOrg = async () => {
    const code = await prompt({ title: t('common:org.joinTitle'), message: t('common:org.joinMessage'), placeholder: 'ABCD2345' });
    if (!code) return;
    await run(async () => {
      const r = await api.join(code);
      toast.info(r.status === 'active' ? t('common:org.alreadyMember', { name: r.name }) : t('common:org.requested', { name: r.name }));
    });
  };
  const copyCode = async () => {
    try { await navigator.clipboard.writeText(inviteCode); toast.success(t('common:org.codeCopied')); } catch (_e) { /* 클립보드 거부 — 코드는 화면에 있다 */ }
  };

  return (
    <div className="org-panel">
      <div className="org-list">
        <div className="org-list-head">
          <span>{t('common:org.mine')}</span>
          <button type="button" className="org-mini" onClick={createOrg} disabled={busy} title={t('common:org.create')}><FiPlus size={12} /></button>
          <button type="button" className="org-mini" onClick={joinOrg} disabled={busy} title={t('common:org.join')}><FiKey size={12} /></button>
        </div>
        {orgs.length === 0 && <div className="org-empty">{t('common:org.none')}</div>}
        {orgs.map(o => (
          <button key={o.id} type="button" className={`org-item ${o.id === selectedId ? 'active' : ''}`} onClick={() => setSelectedId(o.id)}>
            <FiUsers size={13} />
            <span className="org-item-name">{o.name}</span>
            {o.status === 'pending' && <span className="org-badge warn">{t('common:org.pending')}</span>}
            {o.status === 'active' && o.role === 'admin' && <span className="org-badge">{t('common:org.admin')}</span>}
            {o.pending > 0 && <span className="org-count" title={t('common:org.pendingCount', { n: o.pending })}>{o.pending}</span>}
          </button>
        ))}
      </div>
      <div className="org-main">
        {!selected && <div className="org-empty">{t('common:org.pickOrg')}</div>}
        {selected && selected.status === 'pending' && (
          <div className="org-empty">{t('common:org.waitingApproval', { name: selected.name })}</div>
        )}
        {selected && selected.status === 'active' && (
          <>
            <div className="org-head">
              <FiUsers size={14} /> <b>{selected.name}</b>
              <span className="org-muted">{t('common:org.memberCount', { n: selected.members })}</span>
              {isAdmin && (
                <button type="button" className="app-btn" disabled={busy} onClick={async () => {
                  const name = await prompt({ title: t('common:org.renameTitle'), message: t('common:org.createMessage'), defaultValue: selected.name });
                  if (name && name !== selected.name) await run(() => api.rename(selected.id, name));
                }}>{t('common:org.rename')}</button>
              )}
              <button type="button" className="app-btn app-btn-danger" disabled={busy} onClick={async () => {
                const ok = await confirm({ title: t('common:org.leaveTitle'), message: t('common:org.leaveMessage', { name: selected.name }), confirmText: t('common:org.leave'), cancelText: t('common:ui.cancel'), variant: 'danger' });
                if (ok) await run(() => api.leave(selected.id));
              }}><FiLogOut size={12} /> {t('common:org.leave')}</button>
            </div>
            {isAdmin && (
              <div className="org-invite">
                <FiKey size={13} /> <span>{t('common:org.inviteCode')}</span>
                <code>{inviteCode || '…'}</code>
                <button type="button" className="org-mini" onClick={copyCode} disabled={!inviteCode} title={t('common:org.copyCode')}><FiCopy size={12} /></button>
                <button type="button" className="org-mini" disabled={busy} title={t('common:org.regenerate')} onClick={async () => {
                  const ok = await confirm({ title: t('common:org.regenerateTitle'), message: t('common:org.regenerateMessage'), confirmText: t('common:org.regenerate'), cancelText: t('common:ui.cancel') });
                  if (ok) await run(() => api.regenerateInvite(selected.id));
                }}><FiRefreshCw size={12} /></button>
                <span className="org-muted">{t('common:org.inviteHint')}</span>
              </div>
            )}
            <table className="org-table">
              <thead><tr><th>{t('common:org.col.member')}</th><th>{t('common:org.col.role')}</th><th>{t('common:org.col.status')}</th>{isAdmin && <th />}</tr></thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.usrid} className={m.status === 'pending' ? 'pending' : ''}>
                    <td>{(m.person && m.person.name) || m.usrid}{m.usrid === meUsrid ? ` (${t('common:org.me')})` : ''}<span className="org-muted"> {m.person && m.person.login}</span></td>
                    <td>{m.role === 'admin' ? <span className="org-badge"><FiShield size={10} /> {t('common:org.admin')}</span> : t('common:org.member')}</td>
                    <td>{m.status === 'pending' ? <span className="org-badge warn">{t('common:org.pending')}</span> : t('common:org.active')}</td>
                    {isAdmin && (
                      <td className="org-actions">
                        {m.status === 'pending' ? (
                          <>
                            <button type="button" className="org-mini ok" disabled={busy} title={t('common:org.approve')} onClick={() => run(() => api.approve(selected.id, m.usrid), t('common:org.approved', { name: m.usrid }))}><FiCheck size={12} /></button>
                            <button type="button" className="org-mini danger" disabled={busy} title={t('common:org.reject')} onClick={() => run(() => api.reject(selected.id, m.usrid))}><FiX size={12} /></button>
                          </>
                        ) : (
                          <>
                            {m.usrid !== meUsrid && (
                              <button type="button" className="org-mini" disabled={busy} title={m.role === 'admin' ? t('common:org.demote') : t('common:org.promote')}
                                onClick={() => run(() => api.setRole(selected.id, m.usrid, m.role === 'admin' ? 'member' : 'admin'))}><FiShield size={12} /></button>
                            )}
                            {m.usrid !== meUsrid && (
                              <button type="button" className="org-mini danger" disabled={busy} title={t('common:org.removeMember')} onClick={async () => {
                                const ok = await confirm({ title: t('common:org.removeTitle'), message: t('common:org.removeMessage', { name: m.usrid }), confirmText: t('common:org.removeMember'), cancelText: t('common:ui.cancel'), variant: 'danger' });
                                if (ok) await run(() => api.remove(selected.id, m.usrid));
                              }}><FiX size={12} /></button>
                            )}
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {isAdmin && (
              <div className="org-add">
                <FiUserPlus size={13} />
                <input className="org-input" placeholder={t('common:org.addPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
                {found.length > 0 && (
                  <div className="org-found">
                    {found.map(u => <button key={u.usrid} type="button" className="org-found-item" onClick={() => { setQ(''); setFound([]); run(() => api.addMember(selected.id, u.usrid), t('common:org.added', { name: u.name })); }}><b>{u.name}</b> <span className="org-muted">{u.login}</span></button>)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

OrgPanel.propTypes = {
  api: PropTypes.object.isRequired,
  apiBase: PropTypes.string.isRequired,
  meUsrid: PropTypes.string,
  onChanged: PropTypes.func,
};
